import { useEffect, useRef, useState } from "react"
import { Chip, Menu, Tooltip } from "@mui/material"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import type { TranslationKey } from "../../../../../../modules/localization/translations"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { ResourceFilterElement, ResourceFilterElementValue } from "../../../createOrderTypes"
import { PlusMinusButtonFilter } from "./PlusMinusButtonFilter"
import { getResourceFilterTooltip, getResourceFilterValueDisplayLabel } from "./resourceFilterValueLabel"
import { DateTimePickerFilter } from "./DateTimePickerFilter"
import { MultiSelectFilter } from "./MultiSelectFilter"
import { TimeSliderFilter } from "./TimeSliderFilter"
import { SwitcherFilter } from "./SwitcherFilter"
import { FilterIcon } from "./FilterIcon"
import { useFilterChipStyles } from "./filterChipStyles"

type FilterChipProps = {
    filterType: string
    onRemove: () => void
    resourceFiltersResult: ResourceFilterElement[]
    isActive: boolean
    setActiveFilter: (filter: string | null) => void
    initialValues: ResourceFilterElementValue[]
    isRemovable?: boolean
    onSave: (values: ResourceFilterElementValue[]) => void
    onPopoverClose?: () => void
}

function getFilterDisplayName(
    filter: ResourceFilterElement,
    l10n: { getString: (id: TranslationKey) => string },
): string {
    const translated = l10n.getString(filter.translationKey as TranslationKey)
    return translated !== filter.translationKey ? translated : filter.name
}

export function FilterChip({
    filterType,
    onRemove,
    resourceFiltersResult,
    isActive,
    setActiveFilter,
    initialValues,
    onSave,
    onPopoverClose,
    isRemovable = true,
}: FilterChipProps) {
    const { classes, cx } = useFilterChipStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [localValues, setLocalValues] = useState(initialValues)
    const chipRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (isActive && chipRef.current) {
            setAnchorEl(chipRef.current)
        }
    }, [isActive])

    useEffect(() => {
        if (!anchorEl && localValues.length === 0 && !isActive) {
            onRemove()
        }
    }, [anchorEl, localValues, onRemove])

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
        setActiveFilter(filterType)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
        setActiveFilter(null)
        onPopoverClose?.()
    }

    const filterData = resourceFiltersResult.find((filter) => filter.identifier === filterType)

    const handleToggleValue = (value: { key: string | number; value: string | number }) => {
        setLocalValues((prev) => {
            const newValues = prev.some((v) => v.key === value.key)
                ? prev.filter((v) => v.key !== value.key)
                : [...prev, value]
            onSave(newValues)
            return newValues
        })
    }

    const formatDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60)
        const remainingMinutes = minutes % 60
        return `${hours > 0 ? `${hours}h ` : ""}${remainingMinutes > 0 ? `${remainingMinutes}m` : ""}`
    }

    const hasSelectedValues = localValues.length > 0

    const getFilterLabel = (
        filterType: string,
        localValues: ResourceFilterElementValue[],
        hasSelectedValues: boolean,
        filterData?: ResourceFilterElement,
    ) => {
        if (!hasSelectedValues) {
            return filterData ? getFilterDisplayName(filterData, l10n) : filterType
        }

        switch (filterType) {
            case "meetingDuration":
                return localValues.map((v) => formatDuration(Number(v.value))).join(", ")
            case "startDateTime":
                return localValues
                    .map((v) => {
                        const keyAsString = typeof v?.key === "string" ? v.key : String(v?.key)
                        return dateTimeFactory.formatDateTime(dateTimeFactory.fromISO(keyAsString))
                    })
                    .join(", ")

            default:
                return localValues.map((v) => getResourceFilterValueDisplayLabel(filterType, v, l10n)).join(", ")
        }
    }

    const filterName = filterData ? getFilterDisplayName(filterData, l10n) : filterType
    const tooltip = anchorEl ? "" : getResourceFilterTooltip(filterType, localValues, filterName, l10n)

    return (
        <>
            <Tooltip title={tooltip}>
                <Chip
                    ref={chipRef}
                    label={getFilterLabel(filterType, localValues, hasSelectedValues, filterData)}
                    onClick={handleOpenMenu}
                    onDelete={isRemovable ? onRemove : undefined}
                    avatar={<FilterIcon filterType={filterType} isLight={hasSelectedValues} />}
                    className={cx(classes.chip, hasSelectedValues ? classes.selectedChip : classes.unselectedChip)}
                />
            </Tooltip>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
                {(filterData?.elementType === "MultiSelect" || filterData?.elementType === "CheckBox") &&
                    (filterData?.values?.length ?? 0) > 0 && (
                        <MultiSelectFilter
                            identifier={filterType}
                            values={filterData.values ?? []}
                            localValues={localValues}
                            onToggle={handleToggleValue}
                            getValueDisplayLabel={(value) =>
                                getResourceFilterValueDisplayLabel(filterType, value, l10n)
                            }
                        />
                    )}
                {filterData?.elementType === "PlusMinusButton" && (
                    <PlusMinusButtonFilter
                        identifier={filterType}
                        value={localValues[0]?.value !== undefined ? Number(localValues[0]?.value) : null}
                        setValue={(newValue) => {
                            const updatedValues = newValue === null ? [] : [{ key: String(newValue), value: newValue }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
                {filterData?.elementType === "TimeSlider" && (
                    <TimeSliderFilter
                        identifier={filterType}
                        value={localValues[0]?.value !== undefined ? Number(localValues[0]?.value) : null}
                        setValue={(newValue) => {
                            const updatedValues = newValue === null ? [] : [{ key: String(newValue), value: newValue }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
                {filterData?.elementType === "DateTimePicker" && (
                    <DateTimePickerFilter
                        value={localValues[0]?.value !== undefined ? localValues[0]?.value.toString() : null}
                        onClose={handleCloseMenu}
                        setValue={(newValue) => {
                            const updatedValues = [{ key: String(newValue ?? ""), value: newValue ?? 0 }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
                {filterData?.elementType === "Switcher" && (
                    <SwitcherFilter
                        identifier={filterType}
                        value={localValues[0]?.value !== undefined ? localValues[0]?.value.toString() : null}
                        setValue={(newValue) => {
                            const updatedValues = [{ key: newValue, value: newValue }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
            </Menu>
        </>
    )
}
